import { useSuspenseQueries } from "@tanstack/react-query";
import type { Me, Profile, Space, Tenant } from "@repo/shared-types";
import { api } from "./api";
import { requireSessionWith } from "./guards";
import { queries } from "./queries";
import { read } from "./query";
import { holds } from "./grants";
import { viewerZone } from "./times";
import { PictureError, looksLikeAPicture, readCrop, uploadPicture } from "./images";

/**
 * What every page about a space's events needs before it renders: the space,
 * where the caller stands in it, and the zone they read times in.
 *
 * Whether they may manage events decides only what the page offers. The api
 * refuses every write they may not make, and never sends a draft to anyone who
 * may not see one — whatever this says.
 */
export type SpaceContext = {
  session: Me;
  tenant: Tenant;
  role: string;
  grants: string[];
  memberCount: number;
  /** How many ask to join: null unless they may answer, and the cog's badge. */
  requestCount: number | null;
  canManageEvents: boolean;
  /** Whether they may take other people's results off the leaderboards. */
  canModerate: boolean;
  /** Whether they may verify results, and read the queue of those waiting. */
  canVerify: boolean;
  /** Their private profile: the division every result is saved in. */
  profile: Profile;
  zone: string;
};

export async function loadSpaceContext(request: Request, slug: string): Promise<SpaceContext> {
  // A non-member gets 404 from the first two, which is what a missing space
  // gets too. The router's error boundary renders it.
  const [session, [space, profile]] = await requireSessionWith(
    request,
    () => Promise.all([read(queries.space(slug)), read(queries.profile())]),
  );
  return spaceContext(session, space, profile);
}

/**
 * The same, for the page's component: read from the queries its loader
 * warmed through `loadSpaceContext`, so a fresh answer redraws the page.
 */
export function useSpaceContext(slug: string): SpaceContext {
  const [{ data: session }, { data: space }, { data: profile }] = useSuspenseQueries({
    queries: [queries.me(), queries.space(slug), queries.profile()],
  });
  // The loader's guard let the page draw only with a session.
  if (!session) throw new Error("useSpaceContext needs a session");
  return spaceContext(session, space, profile);
}

function spaceContext(session: Me, space: Space, profile: Profile): SpaceContext {
  // By id, not by the path segment: at /t/me the segment names no slug.
  const role = session.memberships.find((m) => m.tenant.id === space.id)?.role ?? "member";
  // The grants, the head count and the request queue's length arrive on the
  // space's own read: no page reads the members list for them (0084).
  const { grants, memberCount, requestCount, ...tenant } = space;
  const { zone } = viewerZone(profile.timeZone);

  return {
    session,
    tenant,
    role,
    grants,
    memberCount,
    requestCount,
    canManageEvents: !tenant.isPersonal && holds(role, grants, "manage_events"),
    canModerate: !tenant.isPersonal && holds(role, grants, "moderate"),
    canVerify: !tenant.isPersonal && holds(role, grants, "verify_scores"),
    profile,
    zone,
  };
}

/** How a status reads, for the few who see anything but "published". */
export function statusLabel(status: string): string {
  switch (status) {
    case "draft":
      return "Draft";
    case "scheduled":
      return "Scheduled";
    case "published":
      return "Published";
    default:
      // The set grows by addition; show what arrived rather than nothing.
      return status;
  }
}

/**
 * What an event's form says about its image (7g), done once the event exists:
 * the file chosen, framed as its box was left, sent and confirmed — or the
 * image there now taken down. Nothing when neither was asked. A new file
 * replaces the image whether or not the box to take it down is ticked.
 */
export async function applyEventImage(slug: string, eventId: string, form: FormData): Promise<"sent" | "removed" | null> {
  const file = form.get("image");
  // A file input with nothing chosen sends an empty file, which asks nothing.
  if (typeof file !== "string" && file !== null && file.size > 0 && !looksLikeAPicture(file)) {
    throw new PictureError("Choose a picture for the event's image.");
  }
  if (looksLikeAPicture(file)) {
    const space = api.space(slug);
    await uploadPicture({
      start: () => space.events.image.start(eventId),
      confirm: (mediaId) => space.media.uploaded(mediaId),
      file,
      crop: readCrop(form.get("imageCrop")),
    });
    return "sent";
  }
  if (form.get("removeImage") === "on") {
    await api.space(slug).events.image.remove(eventId);
    return "removed";
  }
  return null;
}
